import React from 'react';
import { SearchIcon, BellIcon, ChevronDownIcon, HomeIcon, GaugeIcon, BarChart3Icon, WrenchIcon, FileTextIcon, LayoutGridIcon, DropletIcon, LeafIcon, TrendingUpIcon } from 'lucide-react';
import { challengeDashboard as d, type Bi } from '../../data/waterChallenge';
type P = {
  isFR: boolean;
};
// Exact logo files used in the site header (components/Header.tsx)
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NAV_ICONS = {
  overview: LayoutGridIcon,
  properties: HomeIcon,
  meters: GaugeIcon,
  consumption: BarChart3Icon,
  alerts: BellIcon,
  interventions: WrenchIcon,
  reports: FileTextIcon
} as const;
const KPI_ICON = {
  high: {
    Icon: BarChart3Icon,
    bg: '#FDECEC',
    fg: '#E5484D'
  },
  leak: {
    Icon: DropletIcon,
    bg: '#FEF1E6',
    fg: '#EA7A2C'
  },
  irrigation: {
    Icon: LeafIcon,
    bg: '#E8F6EF',
    fg: '#1F9D7A'
  },
  network: {
    Icon: TrendingUpIcon,
    bg: '#EEF0FE',
    fg: '#4F46E5'
  }
} as const;
const PANEL = 'rounded-xl border border-[#EEEEF4] bg-white';
function WeekBars({
  isFR
}: P) {
  const days = isFR ? d.days.fr : d.days.en;
  return <div className="flex gap-2" data-id="element-7170" data-mp="1k0pho3jncj38">
      <div className="flex flex-col justify-between h-[104px] font-sans text-[8.5px] text-[#9AA0B2] pb-4" aria-hidden="true" data-id="element-7171" data-mp="1jxl5aiw12h1h">
        {[225, 150, 75, 0].map(v => <span key={v} data-id="element-7172" data-mp="2co359b1gg2ao">{v}</span>)}
      </div>
      <ul className="flex-1 flex items-end justify-between gap-1.5 h-[104px]" data-id="element-7173" data-mp="27prxr6ymu3vm">
        {d.values.map((v, i) => <li key={days[i]} className="flex-1 flex flex-col items-center gap-1" data-id="element-7174" data-mp="22pnoa0w569ew" data-mpi={i}>
            <span className="w-full max-w-[20px] rounded-t-[3px]" style={{
          height: `${v / 120 * 84}px`,
          background: i === d.values.length - 1 ? '#4F46E5' : '#DCDDFB'
        }} aria-hidden="true" data-id="element-7175" data-mp="1yp3qqw7np3s8" />
            <span className="font-sans text-[8.5px] text-[#8A90A2]" data-id="element-7176" data-mp="n83rthqszqp8">{days[i]}</span>
          </li>)}
      </ul>
    </div>;
}
function AlertMap() {
  return <div className="relative h-[124px] rounded-lg overflow-hidden bg-[#F1F2F6]" aria-hidden="true" data-id="element-7177" data-mp="tq6w1t9zjhiw">
      <svg viewBox="0 0 200 124" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" data-id="element-7178" data-mp="etft5a13rnoj">
        <path d="M150 0 C 160 30, 185 40, 175 70 C 168 95, 190 110, 200 124 L200 0 Z" fill="#CFE0F8" data-id="element-7179" data-mp="jqpspii7hfq3" />
        <path d="M0 40 L200 30 M20 0 L60 124 M0 90 L200 80 M110 0 L130 124 M70 60 L170 110" stroke="#FFFFFF" strokeWidth="3" fill="none" data-id="element-7180" data-mp="5zb7r64qn4ge" />
        <path d="M0 20 L200 60 M40 124 L90 0" stroke="#E2E4EC" strokeWidth="1.5" fill="none" data-id="element-7181" data-mp="at5rg9kcfjfh" />
        <rect x="80" y="70" width="18" height="12" rx="3" fill="#DDEFD9" data-id="element-7182" data-mp="3s5g7eudof4cg" />
        <rect x="24" y="14" width="22" height="14" rx="3" fill="#DDEFD9" data-id="element-7183" data-mp="121c38qr9wk4" />
      </svg>
      {d.markers.map((m, i) => <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-sm flex items-center justify-center" style={{
      left: `${m.x}%`,
      top: `${m.y}%`,
      background: m.tone === 'red' ? '#E5484D' : '#F28C38'
    }} data-id="element-7184" data-mp="3j7o0adi3z305" data-mpi={i}>
          <DropletIcon className="w-2 h-2 text-white" strokeWidth={3} data-id="element-7185" data-mp="2zn5p533sb3m3" />
        </span>)}
      <span className="absolute left-[80%] top-[8%] w-3 h-3 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-7186" data-mp="3dm5e6bv7lkis" />
    </div>;
}
export function ChallengeLaptop({
  isFR
}: P) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div data-id="element-7187" data-mp="3ijylj8890kkn">
      <div className="rounded-t-[22px] bg-[#1B1D26] p-[9px] pb-[12px] shadow-[0_40px_70px_-40px_rgba(20,20,60,0.55)]" data-id="element-7188" data-mp="3ikgnmlhldt0h">
        <div className="rounded-[14px] bg-[#FCFCFE] overflow-hidden" data-id="element-7189" data-mp="4w9vx7bfofn">
          {/* Top bar */}
          <div className="flex items-center gap-3 px-4 h-[52px] bg-white border-b border-[#F2F2F7]" data-id="element-7190" data-mp="3sat524u4162e">
            <img src={BCITI_LOGO} alt="BCITI" className="h-[20px] w-auto object-contain" data-id="element-7191" data-mp="a56ptpkqj9yi" />
            <span className="text-[#121212]/25 font-sans text-sm font-light" aria-hidden="true" data-id="element-7192" data-mp="55tsgaw8mkho">|</span>
            <img src={ATNA_LOGO} alt="ATNA" className="h-[16px] w-auto object-contain" data-id="element-7193" data-mp="p63rh6hxytc8" />
            <div className="hidden sm:flex flex-1 min-w-0 items-center gap-2 rounded-lg bg-[#F5F6FA] px-3 py-1.5 ml-2" data-id="element-7194" data-mp="t7yr86k84ynw">
              <SearchIcon className="w-3 h-3 text-[#9AA0B2] shrink-0" aria-hidden="true" data-id="element-7195" data-mp="y0rwqwniob11" />
              <span className="font-sans text-[10px] text-[#9AA0B2] truncate" data-id="element-7196" data-mp="12xicfivet4lx">{pick(d.search)}</span>
            </div>
            <span className="ml-auto sm:ml-0 flex items-center gap-1.5 rounded-lg border border-[#EEEEF4] px-2.5 py-1.5 font-sans text-[10px] font-medium text-[#121A3A] whitespace-nowrap" data-id="element-7197" data-mp="69vibrlt5ybn">
              {pick(d.org)}
              <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-7198" data-mp="fb21y9jr7p94" />
            </span>
            <BellIcon className="w-3.5 h-3.5 text-[#5B6478] shrink-0" aria-hidden="true" data-id="element-7199" data-mp="k9dc6ldyo1rv" />
            <span className="w-7 h-7 rounded-full bg-[#4F46E5] flex items-center justify-center font-sans text-[9.5px] font-semibold text-white shrink-0" aria-hidden="true" data-id="element-7200" data-mp="1qnmcxhdawo8p">
              LG
            </span>
          </div>
          <div className="flex" data-id="element-7201" data-mp="3g7451ccodjin">
            <ul className="hidden sm:block w-[128px] shrink-0 bg-white border-r border-[#F2F2F7] p-2 space-y-0.5" data-id="element-7202" data-mp="3b5n79pphmd6a">
              {d.nav.map(n => {
              const Icon = NAV_ICONS[n.key];
              const active = n.key === 'overview';
              return <li key={n.key} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 font-sans text-[11px] whitespace-nowrap ${active ? 'bg-[#EEEDFD] text-[#4F46E5] font-semibold' : 'text-[#3A4258]'}`} data-id="element-7203" data-mp="2xes66zl8d0d9">
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-[#4F46E5]' : 'text-[#8A90A2]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-7204" data-mp="2ii12ie4ix2hu" />
                    {pick(n.label)}
                  </li>;
            })}
            </ul>
            <div className="flex-1 min-w-0 p-3.5" data-id="element-7205" data-mp="gqxorfzc77t2">
              <ul className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-7206" data-mp="2b5uvw68gg510">
                {d.kpis.map(k => {
                const {
                  Icon,
                  bg,
                  fg
                } = KPI_ICON[k.key];
                return <li key={k.key} className={`${PANEL} p-2.5 flex items-start gap-2`} data-id="element-7207" data-mp="218p04kowbpug">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{
                    background: bg
                  }} aria-hidden="true" data-id="element-7208" data-mp="2670acwj3s2d7">
                        <Icon className="w-4 h-4" style={{
                      color: fg
                    }} strokeWidth={2} data-id="element-7209" data-mp="2txdy4aunnf1z" />
                      </span>
                      <span className="min-w-0" data-id="element-7210" data-mp="2yturbjgpojlx">
                        <span className="flex items-start gap-1" data-id="element-7211" data-mp="2kwxw75tk8fz8">
                          <span className="font-sans text-[17px] font-semibold text-[#121A3A] leading-none whitespace-nowrap" data-id="element-7212" data-mp="2oxrg7o3vtamu">
                            {k.key === 'network' && !isFR ? '+14%' : k.value}
                          </span>
                          {k.badge && <span className="font-sans text-[8px] font-semibold text-[#E5484D]" data-id="element-7213" data-mp="1dje0j6ja81hw">{pick(k.badge)}</span>}
                        </span>
                        <span className="block font-sans text-[9.5px] text-[#5B6478] mt-1 leading-tight" data-id="element-7214" data-mp="1100gw50go4b2">{pick(k.label)}</span>
                        {k.note && <span className="block font-sans text-[8px] text-[#9AA0B2] leading-tight" data-id="element-7215" data-mp="1fw81v7jp19re">{pick(k.note)}</span>}
                      </span>
                    </li>;
              })}
              </ul>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-7216" data-mp="n8ve4u1sqvwi">
                <div className={`${PANEL} p-3`} data-id="element-7217" data-mp="iak70r2vyzhz">
                  <p className="font-sans text-[11px] font-semibold text-[#121A3A] mb-2" data-id="element-7218" data-mp="wyewzzpieprd">{pick(d.chartTitle)}</p>
                  <WeekBars isFR={isFR} data-id="element-7219" data-mp="s7yiec9ycy3f" />
                </div>
                <div className={`${PANEL} p-3`} data-id="element-7220" data-mp="s6dlwh42v52x">
                  <p className="font-sans text-[11px] font-semibold text-[#121A3A] mb-2" data-id="element-7221" data-mp="4hkrwq70n7ay">{pick(d.mapTitle)}</p>
                  <AlertMap data-id="element-7222" data-mp="3vncddc9ulvrf" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="h-[14px] mx-[-3%] rounded-b-[14px] bg-[#C9CBD3] shadow-[0_24px_40px_-24px_rgba(20,20,60,0.6)]" aria-hidden="true" data-id="element-7223" data-mp="3a6ndq4o253vy" />
    </div>;
}